// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import ArrowLeft from "@insite/mobius/Icons/ArrowLeft";
import { Link } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Page from "@insite/mobius/Page";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useScript } from "@sc/App/Hooks/useScript";
import { CustomAppState } from "@sc/App/Store/Reducers";
import { checkoutPages } from "@sc/XDL/constants";
import { hideElementOnPrint, xScreen } from "@sc/XDL/Utilities";
import { iconLinkCommonStyles } from "@sc/XDL/Utilities";
import * as React from "react";
import { ReactNode } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showCompactHeader: boolean;
    compactHeader: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
    showSignInOverlay?: boolean;
    signInOverlay?: ReactNode;
    showStorePickupOverlay?: boolean;
    storePickupOverlay?: ReactNode;
    showAddressBookOverlay?: boolean;
    addressBookOverlay?: ReactNode;
}

// XNG-Change
const mapStateToProps = (state: CustomAppState) => {
    const pageType: any = getCurrentPage(state).type;
    const { easyLingScript, osanoScript, accessBiScript } = getSettingsCollection(state).customSettings as any;

    return {
        isCheckoutPages: checkoutPages.includes(pageType),
        isOrderConfirmationPage: pageType === "OrderConfirmationPage",
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        easyLingScript,
        osanoScript,
        accessBiScript,
    };
};

// XNG-Changes
export interface PageLayoutStyles {
    breadCrumbsWrapper?: InjectableCss;
    returnToCartLink?: LinkPresentationProps;
}

// XNG-Changes: add styling
export const styles: PageLayoutStyles = {
    breadCrumbsWrapper: {
        css: css`
            ${hideElementOnPrint}
        `,
    },
    returnToCartLink: {
        ...iconLinkCommonStyles(ArrowLeft),
    },
};

// XNG-Change
type Props = PageLayoutProps & ReturnType<typeof mapStateToProps> & HasHistory;

// This doesn't currently support HMR
const PageLayout = ({
    showHeader,
    header,
    showBreadcrumbs,
    pageContent,
    showFooter,
    footer,
    showCompactHeader,
    compactHeader,
    showSignInOverlay,
    signInOverlay,
    showStorePickupOverlay,
    storePickupOverlay,
    showAddressBookOverlay,
    addressBookOverlay,
    // XNG-Change
    isCheckoutPages,
    isOrderConfirmationPage,
    history,
    cartPageUrl,
    osanoScript,
    easyLingScript,
    accessBiScript,
}: Props) => {
    const navigateToCartPage = () => {
        if (cartPageUrl) {
            history.push(cartPageUrl);
        }
    };
    // <XNG-Changes>
    const { isInShell } = useShellContext();
    if (!isInShell) {
        useScript(osanoScript);
        useScript(easyLingScript);
        const accessBiStatus = useScript(accessBiScript);

        React.useEffect(() => {
            if (accessBiStatus === "ready" && typeof window !== "undefined") {
                (window as any).acsbJS.init({
                    statementLink: "",
                    footerHtml: "",
                    hideMobile: true,
                    hideTrigger: true,
                    disableBgProcess: false,
                    language: "en",
                    position: "left",
                    leadColor: "#146ff8",
                    triggerColor: "#146ff8",
                    triggerRadius: "50%",
                    triggerPositionX: "right",
                    triggerPositionY: "bottom",
                    triggerIcon: "people",
                    triggerSize: "medium",
                    triggerOffsetX: 20,
                    triggerOffsetY: 20,
                    mobile: {
                        triggerSize: "small",
                        triggerPositionX: "right",
                        triggerPositionY: "bottom",
                        triggerOffsetX: 10,
                        triggerOffsetY: 10,
                        triggerRadius: "50%",
                    },
                });
            }
        }, [accessBiStatus]);
    }
    // </XNG-Changes>

    return (
        <StyledWrapper
            css={css`
                display: flex;
                flex-direction: column;
                min-height: 100vh;
                @media print {
                    display: block !important;
                }
            `}
        >
            {showHeader && header}
            {showCompactHeader && compactHeader}

            <StyledWrapper
                css={css`
                    background-color: ${getColor("common.white")};
                    flex-grow: 1;
                `}
                className="main-page"
            >
                {showBreadcrumbs && !(isCheckoutPages || isOrderConfirmationPage) && (
                    <Page as="div" {...styles.breadCrumbsWrapper}>
                        <PageBreadcrumbs />
                    </Page>
                )}
                {isCheckoutPages && (
                    <Page
                        as="div"
                        css={css`
                            margin-bottom: 20px;
                            margin-top: 12px;
                        `}
                    >
                        <Link {...styles.returnToCartLink} onClick={navigateToCartPage}>
                            {translate("Back to Cart")}
                        </Link>
                    </Page>
                )}
                {pageContent}
            </StyledWrapper>
            {showFooter && footer}
            {showSignInOverlay && signInOverlay}
            {showStorePickupOverlay && storePickupOverlay}
            {showAddressBookOverlay && addressBookOverlay}
        </StyledWrapper>
    );
};

const PageLayoutWrapper = styled.div`
    display: flex;
    min-height: 100vh;
    flex-direction: column;
    > main {
        flex: 1 1 0%;
    }

    @media print {
        display: block;
    }
`;

export default connect(mapStateToProps)(withHistory(PageLayout));
